import { useState, useEffect, useCallback } from "react";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  COOKIE_CONSENT_CHANGED_EVENT,
  getCookieConsent,
  setCookieConsent,
  type CookieConsent,
} from "@/lib/consent";
import { syncAnalyticsWithConsent } from "@/lib/analytics";

export default function ConsentBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  const showBanner = useCallback(() => {
    setMounted(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setVisible(true);
      });
    });
  }, []);

  const hideBanner = useCallback(() => {
    setVisible(false);
    setTimeout(() => setMounted(false), 300);
  }, []);

  useEffect(() => {
    if (getCookieConsent() === null) {
      showBanner();
    }

    const onConsentChange = (event: Event) => {
      const detail = (event as CustomEvent<{ consent: CookieConsent | null }>).detail;
      if (detail?.consent == null) {
        showBanner();
      } else {
        hideBanner();
      }
    };

    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, onConsentChange);
    return () => window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, onConsentChange);
  }, [showBanner, hideBanner]);

  const choose = (value: CookieConsent) => {
    setCookieConsent(value);
    syncAnalyticsWithConsent();
    hideBanner();
  };

  if (!mounted) return null;

  return (
    <div
      role="alertdialog"
      aria-label="Cookie consent"
      data-testid="banner-consent"
      className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur border-t p-4 transition-transform duration-300 ease-out"
      style={{ transform: visible ? "translateY(0)" : "translateY(100%)" }}
    >
      <Card className="max-w-5xl mx-auto p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <p className="text-sm text-muted-foreground flex-1">
            {t("consent.cookieText")}{" "}
            <Link href="/privacy-policy" className="text-foreground underline">
              {t("consent.privacyPolicy")}
            </Link>
            {" · "}
            <Link href="/cookie-policy" className="text-foreground underline">
              {t("consent.cookiePolicy")}
            </Link>
          </p>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              data-testid="button-essential-cookies"
              onClick={() => choose("essential")}
            >
              {t("consent.essentialOnly")}
            </Button>
            <Button
              data-testid="button-accept-cookies"
              onClick={() => choose("all")}
            >
              {t("consent.acceptAll")}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
